/**
 * Shared primitives (docs/11 §4): Button, Sheet, StatusLine, EmptyState,
 * Skeleton, Chip. Small on purpose — every screen composes these, so the
 * in-vehicle rules (56/64/72 px targets, focus trap, one status line) are
 * enforced here and not re-argued per screen.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import {
  Check, Loader2, LocateOff, RefreshCw, TriangleAlert, WifiOff, Clock,
} from 'lucide-react';

export type ButtonVariant = 'primary' | 'go' | 'secondary' | 'text' | 'quiet' | 'danger' | 'danger-fill';

export function Button({ variant = 'secondary', busy, icon, children, className = '', ...rest }: {
  variant?: ButtonVariant;
  busy?: boolean;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'>) {
  const cls = variant === 'quiet' ? 'text quiet' : variant;
  return (
    <button className={`btn ${cls}${busy ? ' busy' : ''} ${className}`.trim()}
      aria-busy={busy || undefined} {...rest}>
      {busy ? <Loader2 size={20} className="spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

export function Chip({ selected, children, ...rest }: {
  selected: boolean; children: ReactNode;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'>) {
  return (
    <button className="chip" aria-pressed={selected} {...rest}>
      {selected && <Check size={18} aria-hidden="true" />}
      {children}
    </button>
  );
}

/** Modal bottom sheet: aria-modal, focus trap, Escape and scrim close. */
export function Sheet({ title, onClose, children, labelledBy }: {
  title?: string; onClose: () => void; children: ReactNode; labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    opener.current = document.activeElement;
    const el = ref.current;
    const focusables = () => Array.from(
      el?.querySelectorAll<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])') ?? [],
    ).filter((n) => !n.hasAttribute('disabled'));
    (el?.querySelector<HTMLElement>('h2') ?? focusables()[0])?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0]!; const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="scrim" onClick={onClose}>
      <div ref={ref} className="sheet" role="dialog" aria-modal="true"
        aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : title}
        onClick={(e) => e.stopPropagation()}>
        <div className="grab" aria-hidden="true" />
        {title && <h2 id={labelledBy} tabIndex={-1}>{title}</h2>}
        {children}
      </div>
    </div>
  );
}

export type StatusState =
  | { kind: 'none' }
  | { kind: 'finding' }
  | { kind: 'location-off'; onAction: () => void }
  | { kind: 'offline'; asOf: string; onAction: () => void }
  | { kind: 'stale'; agoLabel: string; onAction: () => void };

/** At most one line; it is actionable or it is not shown (docs/11 §3.8). */
export function StatusLine({ state }: { state: StatusState }) {
  switch (state.kind) {
    case 'none': return null;
    case 'finding':
      return (
        <div className="status" role="status">
          <Loader2 size={18} className="spin" aria-hidden="true" /><span className="grow">Finding you…</span>
        </div>
      );
    case 'location-off':
      return (
        <div className="status warn" role="alert">
          <LocateOff size={18} aria-hidden="true" />
          <span className="grow">Location is off, so no leave-by times.</span>
          <button onClick={state.onAction}>Turn on</button>
        </div>
      );
    case 'offline':
      return (
        <div className="status warn" role="status">
          <WifiOff size={18} aria-hidden="true" />
          <span className="grow">No signal — showing {state.asOf} results.</span>
          <button onClick={state.onAction}><RefreshCw size={16} aria-hidden="true" /> Retry</button>
        </div>
      );
    case 'stale':
      return (
        <div className="status" role="status">
          <Clock size={18} aria-hidden="true" />
          <span className="grow">Last updated {state.agoLabel}</span>
          <button onClick={state.onAction}>Refresh</button>
        </div>
      );
  }
}

export function EmptyState({ icon, title, body, children }: {
  icon: ReactNode; title: string; body?: string; children?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon}
      <h2 className="title">{title}</h2>
      {body && <p className="sub">{body}</p>}
      {children}
    </div>
  );
}

export function Skeleton({ variant }: { variant: 'card' | 'row' }) {
  return <div className={`skeleton ${variant}`} aria-hidden="true" />;
}

export function InlineWarn({ children, danger }: { children: ReactNode; danger?: boolean }) {
  return (
    <p className={`inline-note ${danger ? 'danger' : 'warn'}`} role="alert">
      <TriangleAlert size={16} aria-hidden="true" style={{ verticalAlign: -3, marginRight: 6 }} />
      {children}
    </p>
  );
}
